import { useState, useMemo, useCallback, useRef } from 'react'
import { useUnits } from '../../../lib/useUnits'
import { useTranslation } from 'react-i18next'
import { useCatalogItems }                           from '../../../lib/catalogItems'
import { useSubServices, buildSubServiceMap, computeUsageCounts } from '../../../lib/subServices'
import { useSubServiceGroups, COLOR_OPTIONS }         from '../../../lib/subServiceGroups'
import { useCategories, COLOR_CLASSES }              from '../../../lib/categories'
import {
    FaPlus, FaTrash, FaEdit, FaSearch, FaTimes, FaCheck,
    FaExclamationTriangle, FaChevronDown, FaChevronUp,
    FaSync, FaTag, FaLayerGroup, FaCheckCircle, FaTimesCircle,
} from 'react-icons/fa'

// ── Colour swatch picker ──────────────────────────────────────────────────────
function ColorPicker({ value, onChange }) {
    return (
        <div className="flex items-center gap-1.5 flex-wrap">
            {COLOR_OPTIONS.map(c => (
                <button
                    key={c}
                    type="button"
                    title={c}
                    onClick={() => onChange(c)}
                    className={`w-5 h-5 rounded-full border-2 transition-all ${COLOR_CLASSES[c]?.dot ?? 'bg-gray-400'} ${value === c ? 'border-gray-700 scale-125' : 'border-transparent opacity-50 hover:opacity-100'}`}
                />
            ))}
        </div>
    )
}

// ── Confirm delete dialog ─────────────────────────────────────────────────────
function ConfirmDialog({ title, body, confirmLabel, onConfirm, onCancel, danger = true }) {
    const { t } = useTranslation()
    const resolvedLabel = confirmLabel ?? t('admin.sub_services.delete_confirm')
    return (
        <div className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black/40">
            <div className="p-6 w-full max-w-sm bg-white rounded-2xl shadow-2xl">
                <div className="flex gap-3 items-start mb-4">
                    <FaExclamationTriangle className="text-amber-500 mt-0.5 shrink-0" size={18} />
                    <div>
                        <h3 className="text-base font-bold text-gray-900">{title}</h3>
                        <p className="mt-1 text-sm text-gray-500">{body}</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={onConfirm}
                        className={`flex-1 py-2 text-sm font-bold rounded-xl transition-all ${danger ? 'text-white bg-red-500 hover:bg-red-600' : 'text-white bg-primary hover:bg-primary/90'}`}
                    >
                        {resolvedLabel}
                    </button>
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 text-sm font-bold text-gray-600 bg-gray-100 rounded-xl transition-all hover:bg-gray-200"
                    >
                        {t('admin.sub_services.cancel')}
                    </button>
                </div>
            </div>
        </div>
    )
}

// ── Reassign category modal ───────────────────────────────────────────────────
function ReassignCategoryModal({ deletingCat, itemCount, otherCats, onConfirm, onCancel }) {
    const { t } = useTranslation()
    const [target, setTarget] = useState(otherCats[0]?.name || '')
    return (
        <div className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black/40">
            <div className="p-6 w-full max-w-sm bg-white rounded-2xl shadow-2xl">
                <div className="flex gap-3 items-start mb-4">
                    <FaExclamationTriangle className="text-amber-500 mt-0.5 shrink-0" size={18} />
                    <div>
                        <h3 className="text-base font-bold text-gray-900">{t('admin.sub_services.reassign_title')}</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            {t('admin.sub_services.reassign_body', { count: itemCount, cat: deletingCat })}
                        </p>
                    </div>
                </div>
                <select
                    value={target}
                    onChange={e => setTarget(e.target.value)}
                    className="px-3 py-2 mb-4 w-full text-sm bg-white rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                >
                    {otherCats.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
                <div className="flex gap-2">
                    <button
                        onClick={() => onConfirm(target)}
                        disabled={!target}
                        className="flex-1 py-2 text-sm font-bold text-white bg-red-500 rounded-xl transition-all hover:bg-red-600 disabled:opacity-50"
                    >
                        {t('admin.sub_services.reassign_delete')}
                    </button>
                    <button onClick={onCancel} className="px-4 py-2 text-sm font-bold text-gray-600 bg-gray-100 rounded-xl transition-all hover:bg-gray-200">{t('admin.sub_services.cancel')}</button>
                </div>
            </div>
        </div>
    )
}

// ── Mini sub-service picker for inline panel ──────────────────────────────────
function MiniPicker({ allSubServices, ids, oppositeIds, onIdsChange, type }) {
    const [input,   setInput]   = useState('')
    const { t } = useTranslation()
    const [show,    setShow]    = useState(false)
    const [dropPos, setDropPos] = useState(null)
    const inputRef = useRef(null)
    const label     = type === 'inc' ? t('admin.sub_services.col_included') : t('admin.sub_services.col_excluded')
    const pillClass = type === 'inc' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-red-200 bg-red-50 text-red-700'

    const computeDropPos = () => {
        if (inputRef.current) {
            const r = inputRef.current.getBoundingClientRect()
            setDropPos({ top: r.bottom + 4, left: r.left, width: r.width })
        }
    }

    const suggestions = useMemo(() => {
        const q = input.trim().toLowerCase()
        if (!q) return []
        return allSubServices
            .filter(s => !ids.includes(s.id) && !oppositeIds.includes(s.id) && s.text.toLowerCase().includes(q))
            .slice(0, 6)
    }, [input, allSubServices, ids, oppositeIds])

    const subMap = useMemo(() => buildSubServiceMap(allSubServices), [allSubServices])

    return (
        <div>
            <div className="flex flex-wrap gap-1 mb-1">
                {ids.map(id => (
                    <span key={id} className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border ${pillClass}`}>
                        <span className="max-w-[160px] truncate">{subMap[id]?.text ?? id.slice(0, 8)}</span>
                        <button type="button" onClick={() => onIdsChange(ids.filter(i => i !== id))} className="text-gray-400 hover:text-red-500 ml-0.5">
                            <FaTimesCircle size={9} />
                        </button>
                    </span>
                ))}
            </div>
            <div className="relative">
                <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={e => { setInput(e.target.value); computeDropPos(); setShow(true) }}
                    onFocus={() => { computeDropPos(); setShow(true) }}
                    onBlur={() => setTimeout(() => setShow(false), 180)}
                    placeholder={`${t('admin.sub_services.add')} ${label.toLowerCase()}…`}
                    className="px-2 py-1 text-[10px] rounded border border-gray-200 focus:outline-none focus:ring-1 focus:ring-primary/40 w-full"
                />
                {show && suggestions.length > 0 && dropPos && (
                    <div style={{ position: 'fixed', top: dropPos.top, left: dropPos.left, width: dropPos.width }} className="z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden max-h-40 overflow-y-auto">
                        {suggestions.map(s => (
                            <button key={s.id} type="button" onMouseDown={() => { onIdsChange([...ids, s.id]); setInput(''); setShow(false) }}
                                className="w-full px-2 py-1.5 text-left text-[10px] text-gray-700 hover:bg-primary/5 truncate">
                                {s.text}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}

// ── Single item row inside the usage panel ────────────────────────────────────
function UsageItemRow({ item, allSubServices, upsertItem }) {
    const [localInc, setLocalInc] = useState([...(item.inc_ids || [])])
    const [localExc, setLocalExc] = useState([...(item.exc_ids || [])])
    const { t } = useTranslation()
    const [saving,   setSaving]   = useState(false)
    const [saved,    setSaved]    = useState(false)

    const isDirty = JSON.stringify(localInc) !== JSON.stringify(item.inc_ids || []) ||
                    JSON.stringify(localExc) !== JSON.stringify(item.exc_ids || [])

    const handleSave = async () => {
        setSaving(true)
        const incTexts = localInc.map(id => allSubServices.find(s => s.id === id)?.text || '')
        const excTexts = localExc.map(id => allSubServices.find(s => s.id === id)?.text || '')
        try {
            await upsertItem({ ...item, inc_ids: localInc, inc: incTexts, exc_ids: localExc, exc: excTexts })
            setSaved(true)
            setTimeout(() => setSaved(false), 2000)
        } catch (e) { console.error(e) }
        finally { setSaving(false) }
    }

    return (
        <div className="p-3 bg-white rounded-lg border border-gray-200">
            <div className="flex gap-2 justify-between items-center mb-2">
                <div className="min-w-0">
                    <p className="text-xs font-semibold text-gray-800 truncate">{item.name}</p>
                    <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wide">{item.cat}</span>
                </div>
                <div className="flex gap-1.5 shrink-0">
                    {isDirty && (
                        <button onClick={handleSave} disabled={saving}
                            className="flex items-center gap-1 px-2 py-1 text-[10px] font-bold bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-all">
                            <FaCheck size={8} /> {saving ? t('admin.sub_services.saving') : t('admin.sub_services.save')}
                        </button>
                    )}
                    {saved && !isDirty && (
                        <span className="flex items-center gap-1 text-[10px] text-emerald-600 font-semibold">
                            <FaCheckCircle size={9} /> {t('admin.sub_services.saved')}
                        </span>
                    )}
                </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
                <div>
                    <p className="text-[9px] font-bold text-emerald-600 uppercase tracking-wide mb-1">{t('admin.sub_services.col_included')}</p>
                    <MiniPicker allSubServices={allSubServices} ids={localInc} oppositeIds={localExc} type="inc" onIdsChange={setLocalInc} />
                </div>
                <div>
                    <p className="text-[9px] font-bold text-red-500 uppercase tracking-wide mb-1">{t('admin.sub_services.col_excluded')}</p>
                    <MiniPicker allSubServices={allSubServices} ids={localExc} oppositeIds={localInc} type="exc" onIdsChange={setLocalExc} />
                </div>
            </div>
        </div>
    )
}

// ── Usage panel: cat → items collapsible tree ────────────────────────────────
function UsagePanelTree({ usageEntry, allSubServices, allCatalogItems, upsertItem }) {
    const [openCats, setOpenCats] = useState({})

    const grouped = useMemo(() => {
        if (!usageEntry || usageEntry.total === 0) return {}
        const seen = new Set()
        const tree = {}
        for (const slim of usageEntry.items) {
            if (seen.has(slim.id)) continue
            seen.add(slim.id)
            const full = allCatalogItems.find(ci => ci.id === slim.id) || slim
            const cat  = full.cat || '(no category)'
            if (!tree[cat]) tree[cat] = []
            tree[cat].push(full)
        }
        return tree
    }, [usageEntry, allCatalogItems])

    const { t } = useTranslation()
    if (!usageEntry || usageEntry.total === 0) {
        return <p className="py-2 text-xs italic text-gray-400">{t('admin.sub_services.not_used')}</p>
    }

    const cats = Object.keys(grouped).sort()

    return (
        <div className="mt-1 space-y-1">
            {cats.map(cat => {
                const items     = grouped[cat]
                const catOpen   = openCats[cat] ?? false
                const toggleCat = () => setOpenCats(p => ({ ...p, [cat]: !p[cat] }))
                return (
                    <div key={cat} className="rounded-lg border border-gray-100">
                        {/* Cat header */}
                        <button
                            onClick={toggleCat}
                            className="flex gap-2 items-center px-3 py-2 w-full text-left bg-gray-50 transition-colors hover:bg-gray-100"
                        >
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">{cat}</span>
                            <span className="text-[10px] text-gray-400 font-medium">{t('admin.sub_services.item_count', { count: items.length })}</span>
                            <span className="ml-auto text-gray-400">
                                {catOpen ? <FaChevronUp size={9} /> : <FaChevronDown size={9} />}
                            </span>
                        </button>

                        {/* Items */}
                        {catOpen && (
                            <div className="px-3 pt-1 pb-2 space-y-2">
                                {items.map(item => (
                                    <UsageItemRow
                                        key={item.id}
                                        item={item}
                                        allSubServices={allSubServices}
                                        upsertItem={upsertItem}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )
            })}
        </div>
    )
}

// ── Quick-assign: add this sub-service to any catalog item ─────────────
function QuickAssignSection({ sub, allCatalogItems, usageEntry, upsertItem }) {
    const { t } = useTranslation()
    const [query,  setQuery]  = useState('')
    const [saving, setSaving] = useState(null)

    const usedIds = new Set((usageEntry?.items || []).map(i => i.id))

    const results = useMemo(() => {
        const q = query.trim().toLowerCase()
        return allCatalogItems
            .filter(it => !usedIds.has(it.id) && (!q || it.name.toLowerCase().includes(q) || it.cat?.toLowerCase().includes(q)))
            .slice(0, 8)
    }, [allCatalogItems, usedIds, query])

    const assign = async (item, type) => {
        setSaving(item.id + type)
        const incIds = [...(item.inc_ids || [])]
        const excIds = [...(item.exc_ids || [])]
        if (type === 'inc' && !incIds.includes(sub.id)) incIds.push(sub.id)
        if (type === 'exc' && !excIds.includes(sub.id)) excIds.push(sub.id)
        try {
            await upsertItem({
                ...item,
                inc_ids: incIds,
                inc: incIds.map(id => {
                    if (id === sub.id) return sub.text
                    return item.inc?.[(item.inc_ids || []).indexOf(id)] || ''
                }),
                exc_ids: excIds,
                exc: excIds.map(id => {
                    if (id === sub.id) return sub.text
                    return item.exc?.[(item.exc_ids || []).indexOf(id)] || ''
                }),
            })
            setQuery('')
        } catch (e) { console.error(e) }
        finally { setSaving(null) }
    }

    return (
        <div className="pt-3 mt-3 border-t border-gray-200">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-2">{t('admin.sub_services.quick_assign')}</p>
            <div className="relative mb-2">
                <FaSearch className="absolute left-2 top-1/2 text-gray-400 -translate-y-1/2" size={9} />
                <input
                    type="text"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    placeholder={t('admin.sub_services.quick_assign_placeholder')}
                    className="pl-6 pr-2 py-1 w-full text-[10px] rounded border border-gray-200 focus:outline-none focus:ring-1 focus:ring-primary/40"
                />
            </div>
            {query.trim() && results.length === 0 && (
                <p className="text-[10px] text-gray-400 italic">{t('admin.sub_services.quick_assign_none')}</p>
            )}
            <div className="space-y-1">
                {results.map(item => (
                    <div key={item.id} className="flex gap-2 items-center py-1">
                        <p className="flex-1 min-w-0 text-[10px] text-gray-700 truncate">
                            <span className="mr-1 font-semibold text-gray-400">{item.cat}</span>{item.name}
                        </p>
                        <button
                            onClick={() => assign(item, 'inc')}
                            disabled={saving === item.id + 'inc'}
                            className="px-2 py-0.5 text-[9px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full hover:bg-emerald-100 disabled:opacity-50 transition-all"
                        >{saving === item.id + 'inc' ? '…' : t('admin.sub_services.col_included')}</button>
                        <button
                            onClick={() => assign(item, 'exc')}
                            disabled={saving === item.id + 'exc'}
                            className="px-2 py-0.5 text-[9px] font-bold text-red-600 bg-red-50 border border-red-200 rounded-full hover:bg-red-100 disabled:opacity-50 transition-all"
                        >{saving === item.id + 'exc' ? '…' : t('admin.sub_services.col_excluded')}</button>
                    </div>
                ))}
            </div>
        </div>
    )
}

// ── Sub-service row ───────────────────────────────────────────────────────
function SubServiceRow({ sub, groups, usageEntry, allSubServices, allCatalogItems, upsertItem, onUpdate, onDelete }) {
    const [editing,     setEditing]     = useState(false)
    const [editText,    setEditText]    = useState(sub.text)
    const [editGroup,   setEditGroup]   = useState(sub.group_id || '')
    const [savingEdit,  setSavingEdit]  = useState(false)
    const [panelOpen,   setPanelOpen]   = useState(false)
    const [confirmDel,  setConfirmDel]  = useState(false)
    const [deleting,    setDeleting]    = useState(false)
    const inputRef = useRef(null)

    const { t } = useTranslation()
    const usage   = usageEntry ?? { total: 0, inc: 0, exc: 0, items: [] }
    const groupObj = groups.find(g => g.id === sub.group_id)

    const textChanged   = editText.trim() !== sub.text
    const cascadeCount  = textChanged ? (usageEntry?.total ?? 0) : 0

    const handleSaveEdit = async () => {
        const text = editText.trim()
        if (!text) return
        setSavingEdit(true)
        try {
            await onUpdate(sub.id, { text, group_id: editGroup || null, _textChanged: textChanged })
            setEditing(false)
        } catch (e) { console.error(e) }
        finally { setSavingEdit(false) }
    }

    const handleDelete = async () => {
        setDeleting(true)
        try { await onDelete(sub.id, usage.items) }
        catch (e) { console.error(e) }
        finally { setDeleting(false); setConfirmDel(false) }
    }

    return (
        <>
            <div className="border-b border-gray-100 last:border-0">
                <div className="flex items-center gap-2 px-3 py-2.5 hover:bg-gray-50 transition-colors">
                    {/* Group colour dot */}
                    <div
                        className={`w-2.5 h-2.5 rounded-full shrink-0 ${groupObj ? (COLOR_CLASSES[groupObj.color]?.dot ?? 'bg-gray-300') : 'bg-gray-200'}`}
                        title={groupObj?.name ?? t('admin.sub_services.ungrouped')}
                    />

                    {/* Text — inline edit */}
                    {editing ? (
                        <div className="flex flex-col flex-1 gap-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                                <input
                                    ref={inputRef}
                                    autoFocus
                                    value={editText}
                                    onChange={e => setEditText(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') { setEditing(false); setEditText(sub.text) } }}
                                    className="flex-1 px-2 py-1 min-w-0 text-xs rounded border border-primary/40 focus:outline-none focus:ring-1 focus:ring-primary/40"
                                />
                                <select
                                    value={editGroup}
                                    onChange={e => setEditGroup(e.target.value)}
                                    className="px-2 py-1 text-[10px] rounded border border-gray-200 focus:outline-none bg-white"
                                >
                                    <option value="">{t('admin.sub_services.ungrouped')}</option>
                                    {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                                </select>
                                <button onClick={handleSaveEdit} disabled={savingEdit} className="p-1.5 rounded bg-primary text-white hover:bg-primary/90 disabled:opacity-50 transition-all" title={cascadeCount > 0 ? t('admin.sub_services.cascade_warning', { count: cascadeCount }) : t('admin.sub_services.save')}>
                                    {savingEdit ? <FaSync size={9} className="animate-spin" /> : <FaCheck size={9} />}
                                </button>
                                <button onClick={() => { setEditing(false); setEditText(sub.text); setEditGroup(sub.group_id || '') }} className="p-1.5 rounded bg-gray-100 text-gray-500 hover:bg-gray-200 transition-all">
                                    <FaTimes size={9} />
                                </button>
                            </div>
                            {cascadeCount > 0 && (
                                <p className="text-[9px] text-amber-600 font-medium pl-0.5">
                                    {t('admin.sub_services.cascade_warning', { count: cascadeCount })}
                                </p>
                            )}
                        </div>
                    ) : (
                        <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-800 truncate">{sub.text}</p>
                            {groupObj && <p className="text-[9px] text-gray-400">{groupObj.name}</p>}
                        </div>
                    )}

                    {/* Usage badge */}
                    {!editing && (
                        <button
                            onClick={() => setPanelOpen(p => !p)}
                            className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold border transition-all shrink-0 ${
                                usage.total > 0
                                    ? 'border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100'
                                    : 'border-gray-200 bg-gray-50 text-gray-400 hover:bg-gray-100'
                            }`}
                            title={t('admin.sub_services.usage_hint')}
                        >
                            {usage.total > 0 && (
                                <>
                                    <span className="text-emerald-600">{usage.inc}↑</span>
                                    <span className="text-gray-300">·</span>
                                    <span className="text-red-500">{usage.exc}↓</span>
                                </>
                            )}
                            {usage.total === 0 && <span>{t('admin.sub_services.unused')}</span>}
                            {panelOpen ? <FaChevronUp size={8} className="ml-0.5" /> : <FaChevronDown size={8} className="ml-0.5" />}
                        </button>
                    )}

                    {/* Actions */}
                    {!editing && (
                        <div className="flex gap-1 shrink-0">
                            <button onClick={() => { setEditing(true); setPanelOpen(false) }} className="p-1.5 rounded text-gray-400 hover:text-primary hover:bg-primary/5 transition-all">
                                <FaEdit size={10} />
                            </button>
                            <button
                                onClick={() => setConfirmDel(true)}
                                disabled={deleting}
                                className="p-1.5 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all disabled:opacity-40"
                            >
                                <FaTrash size={10} />
                            </button>
                        </div>
                    )}
                </div>

                {/* Inline usage panel */}
                {panelOpen && (
                    <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-2">
                            {t('admin.sub_services.used_in', { count: usage.total })}
                        </p>
                        <UsagePanelTree
                            usageEntry={usage}
                            allSubServices={allSubServices}
                            allCatalogItems={allCatalogItems}
                            upsertItem={upsertItem}
                        />
                        <QuickAssignSection
                            sub={sub}
                            allCatalogItems={allCatalogItems}
                            usageEntry={usage}
                            upsertItem={upsertItem}
                        />
                    </div>
                )}
            </div>

            {confirmDel && (
                <ConfirmDialog
                    title={t('admin.sub_services.delete_sub_title')}
                    body={
                        usage.total > 0
                            ? t('admin.sub_services.delete_sub_body_used', { count: usage.total })
                            : t('admin.sub_services.delete_sub_body_unused', { text: sub.text })
                    }
                    onConfirm={handleDelete}
                    onCancel={() => setConfirmDel(false)}
                />
            )}
        </>
    )
}

// ── Categories section ────────────────────────────────────────────────────────
function CategoriesSection({ categories, catalogItems, updateCategory, deleteCategory, createCategory, reload }) {
    const { t } = useTranslation()
    const [addingNew,   setAddingNew]   = useState(false)
    const [newName,     setNewName]     = useState('')
    const [newColor,    setNewColor]    = useState('gray')
    const [creating,    setCreating]    = useState(false)
    const [editingId,   setEditingId]   = useState(null)
    const [editName,    setEditName]    = useState('')
    const [editColor,   setEditColor]   = useState('gray')
    const [savingEdit,  setSavingEdit]  = useState(false)
    const [reassignFor, setReassignFor] = useState(null) // { cat, count, otherCats }

    const itemCountByCat = useMemo(() => {
        const m = {}
        for (const it of catalogItems) m[it.cat] = (m[it.cat] || 0) + 1
        return m
    }, [catalogItems])

    const handleCreate = async () => {
        const name = newName.trim().toUpperCase()
        if (!name) return
        setCreating(true)
        try { await createCategory({ name, color: newColor }); setNewName(''); setNewColor('gray'); setAddingNew(false) }
        catch (e) { console.error(e) }
        finally { setCreating(false) }
    }

    const handleSaveEdit = async (id) => {
        const name = editName.trim().toUpperCase()
        if (!name) return
        setSavingEdit(true)
        try { await updateCategory(id, { name, color: editColor }); setEditingId(null) }
        catch (e) { console.error(e) }
        finally { setSavingEdit(false) }
    }

    const handleDeleteClick = (cat) => {
        const count = itemCountByCat[cat.name] || 0
        if (count === 0) {
            setReassignFor({ cat, count: 0, otherCats: categories.filter(c => c.id !== cat.id) })
        } else {
            setReassignFor({ cat, count, otherCats: categories.filter(c => c.id !== cat.id) })
        }
    }

    const handleConfirmDelete = async (targetCatName) => {
        const { cat, count } = reassignFor
        try {
            if (count > 0 && targetCatName) {
                const affected = catalogItems.filter(it => it.cat === cat.name)
                for (const it of affected) {
                    await reload('reassign', it, targetCatName)
                }
            }
            await deleteCategory(cat.id)
        } catch (e) { console.error(e) }
        finally { setReassignFor(null) }
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-3">
                <div className="flex gap-2 items-center">
                    <FaTag size={13} className="text-gray-400" />
                    <h3 className="text-sm font-bold text-gray-700">{t('admin.sub_services.categories_title')}</h3>
                    <span className="text-[10px] text-gray-400 font-medium">{categories.length}</span>
                </div>
                <button
                    onClick={() => setAddingNew(p => !p)}
                    className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold text-primary border border-primary/30 rounded-lg hover:bg-primary/5 transition-all"
                >
                    <FaPlus size={9} /> {t('admin.sub_services.add')}
                </button>
            </div>

            {addingNew && (
                <div className="p-3 mb-3 space-y-2 rounded-xl border border-primary/20 bg-primary/5">
                    <input
                        autoFocus
                        type="text"
                        value={newName}
                        onChange={e => setNewName(e.target.value.toUpperCase())}
                        onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') setAddingNew(false) }}
                        placeholder={t('admin.sub_services.placeholder_cat_name')}
                        className="px-3 py-1.5 w-full text-xs font-bold tracking-wide rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-primary/40"
                    />
                    <ColorPicker value={newColor} onChange={setNewColor} />
                    <div className="flex gap-1.5">
                        <button onClick={handleCreate} disabled={!newName.trim() || creating}
                            className="flex-1 py-1.5 bg-primary text-white text-xs font-bold rounded-lg disabled:opacity-50 transition-all">
                            {creating ? t('admin.sub_services.creating') : t('admin.sub_services.create')}
                        </button>
                        <button onClick={() => { setAddingNew(false); setNewName('') }}
                            className="px-3 py-1.5 bg-gray-100 text-gray-600 text-xs font-bold rounded-lg hover:bg-gray-200 transition-all">{t('admin.sub_services.cancel')}</button>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {categories.map(cat => {
                    const col   = (typeof COLOR_CLASSES[cat.color] !== 'undefined') ? COLOR_CLASSES[cat.color] : COLOR_CLASSES.gray
                    const count = itemCountByCat[cat.name] || 0
                    const isEditing = editingId === cat.id
                    return (
                        <div key={cat.id} className={`rounded-xl border p-3 ${col.bg} border-gray-200`}>
                            {isEditing ? (
                                <div className="space-y-2">
                                    <input
                                        autoFocus
                                        value={editName}
                                        onChange={e => setEditName(e.target.value.toUpperCase())}
                                        onKeyDown={e => { if (e.key === 'Enter') handleSaveEdit(cat.id); if (e.key === 'Escape') setEditingId(null) }}
                                        onBlur={() => {
                                            const name = editName.trim().toUpperCase()
                                            if (!name || name === cat.name) return
                                            const existing = categories.find(c => c.id !== cat.id && c.name === name)
                                            if (existing) {
                                                alert(t('admin.error.duplicateName') || 'Name already exists')
                                            }
                                        }}
                                        className="px-2 py-1 w-full text-xs font-bold tracking-wide rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-primary/40"
                                    />
                                    <ColorPicker value={editColor} onChange={setEditColor} />
                                    <div className="flex gap-1">
                                        <button onClick={() => handleSaveEdit(cat.id)} disabled={savingEdit}
                                            className="flex-1 py-1 bg-primary text-white text-[10px] font-bold rounded-lg disabled:opacity-50 transition-all">
                                            {savingEdit ? t('admin.sub_services.saving') : t('admin.sub_services.save')}
                                        </button>
                                        <button onClick={() => setEditingId(null)}
                                            className="px-2 py-1 bg-white text-gray-500 text-[10px] font-bold rounded-lg hover:bg-gray-100 transition-all">{t('admin.sub_services.cancel')}</button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex gap-2 items-center">
                                    <div className={`w-3 h-3 rounded-full shrink-0 ${col.dot}`} />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-bold text-gray-800 truncate">{cat.name}</p>
                                        <p className="text-[9px] text-gray-500">{t('admin.sub_services.item_count', { count })}</p>
                                    </div>
                                    <div className="flex gap-1 shrink-0">
                                        <button onClick={() => { setEditingId(cat.id); setEditName(cat.name); setEditColor(cat.color) }}
                                            className="p-1 text-gray-400 rounded transition-all hover:text-primary hover:bg-white/60">
                                            <FaEdit size={10} />
                                        </button>
                                        <button onClick={() => handleDeleteClick(cat)}
                                            className="p-1 text-gray-400 rounded transition-all hover:text-red-500 hover:bg-white/60">
                                            <FaTrash size={10} />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>

            {reassignFor && reassignFor.count === 0 && (
                <ConfirmDialog
                    title={t('admin.sub_services.delete_empty_title', { name: reassignFor.cat.name })}
                    body={t('admin.sub_services.delete_empty_body')}
                    onConfirm={() => handleConfirmDelete(null)}
                    onCancel={() => setReassignFor(null)}
                />
            )}
            {reassignFor && reassignFor.count > 0 && (
                <ReassignCategoryModal
                    deletingCat={reassignFor.cat.name}
                    itemCount={reassignFor.count}
                    otherCats={reassignFor.otherCats}
                    onConfirm={handleConfirmDelete}
                    onCancel={() => setReassignFor(null)}
                />
            )}
        </div>
    )
}

// ── Sub-service groups section ────────────────────────────────────────────────
function GroupsSection({ groups, subServices, createGroup, updateGroup, deleteGroup }) {
    const { t } = useTranslation()
    const [open,       setOpen]       = useState(false)
    const [addingNew,  setAddingNew]  = useState(false)
    const [newName,    setNewName]    = useState('')
    const [newColor,   setNewColor]   = useState('gray')
    const [creating,   setCreating]   = useState(false)
    const [editingId,  setEditingId]  = useState(null)
    const [editName,   setEditName]   = useState('')
    const [editColor,  setEditColor]  = useState('gray')
    const [savingEdit, setSavingEdit] = useState(false)
    const [confirmDel, setConfirmDel] = useState(null)
    const [deleting,   setDeleting]   = useState(false)

    const countByGroup = useMemo(() => {
        const m = {}
        for (const s of subServices) {
            if (s.group_id) m[s.group_id] = (m[s.group_id] || 0) + 1
        }
        return m
    }, [subServices])

    const handleCreate = async () => {
        const name = newName.trim().toUpperCase()
        if (!name) return
        setCreating(true)
        try { await createGroup({ name, color: newColor }); setNewName(''); setNewColor('gray'); setAddingNew(false) }
        catch (e) { console.error(e) }
        finally { setCreating(false) }
    }

    const handleSaveEdit = async (id) => {
        const name = editName.trim().toUpperCase()
        if (!name) return
        setSavingEdit(true)
        try { await updateGroup(id, { name, color: editColor }); setEditingId(null) }
        catch (e) { console.error(e) }
        finally { setSavingEdit(false) }
    }

    const handleDelete = async (group) => {
        setDeleting(true)
        try { await deleteGroup(group.id) }
        catch (e) { console.error(e) }
        finally { setDeleting(false); setConfirmDel(null) }
    }

    return (
        <div>
            <button
                onClick={() => setOpen(p => !p)}
                className="flex gap-2 items-center py-2 w-full text-left group"
            >
                <FaLayerGroup size={13} className="text-gray-400" />
                <span className="text-sm font-bold text-gray-700">{t('admin.sub_services.groups_title')}</span>
                <span className="text-[10px] text-gray-400 font-medium">{groups.length}</span>
                <span className="ml-auto text-gray-400 transition-colors group-hover:text-gray-600">
                    {open ? <FaChevronUp size={11} /> : <FaChevronDown size={11} />}
                </span>
            </button>

            {open && (
                <div className="mt-2 space-y-2">
                    {addingNew && (
                        <div className="p-3 space-y-2 rounded-xl border border-primary/20 bg-primary/5">
                            <input autoFocus type="text" value={newName} onChange={e => setNewName(e.target.value.toUpperCase())}
                                onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') setAddingNew(false) }}
                                placeholder={t('admin.sub_services.placeholder_group_name')}
                                className="px-3 py-1.5 w-full text-xs font-bold tracking-wide rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-primary/40"
                            />
                            <ColorPicker value={newColor} onChange={setNewColor} />
                            <div className="flex gap-1.5">
                                <button onClick={handleCreate} disabled={!newName.trim() || creating}
                                    className="flex-1 py-1.5 bg-primary text-white text-xs font-bold rounded-lg disabled:opacity-50 transition-all">
                                    {creating ? t('admin.sub_services.creating') : t('admin.sub_services.create')}
                                </button>
                                <button onClick={() => { setAddingNew(false); setNewName('') }}
                                    className="px-3 py-1.5 bg-gray-100 text-gray-600 text-xs font-bold rounded-lg hover:bg-gray-200 transition-all">{t('admin.sub_services.cancel')}</button>
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                        {groups.map(g => {
                            const col   = COLOR_CLASSES[g.color] ?? COLOR_CLASSES.gray
                            const count = countByGroup[g.id] || 0
                            return (
                                <div key={g.id} className={`rounded-xl border p-3 ${col.bg} border-gray-200`}>
                                    {editingId === g.id ? (
                                        <div className="space-y-2">
                                            <input autoFocus value={editName} onChange={e => setEditName(e.target.value.toUpperCase())}
                                                onKeyDown={e => { if (e.key === 'Enter') handleSaveEdit(g.id); if (e.key === 'Escape') setEditingId(null) }}
                                                className="px-2 py-1 w-full text-xs font-bold tracking-wide rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-primary/40"
                                            />
                                            <ColorPicker value={editColor} onChange={setEditColor} />
                                            <div className="flex gap-1">
                                                <button onClick={() => handleSaveEdit(g.id)} disabled={savingEdit}
                                                    className="flex-1 py-1 bg-primary text-white text-[10px] font-bold rounded-lg disabled:opacity-50">
                                                    {savingEdit ? t('admin.sub_services.saving') : t('admin.sub_services.save')}
                                                </button>
                                                <button onClick={() => setEditingId(null)} className="px-2 py-1 bg-white text-gray-500 text-[10px] font-bold rounded-lg hover:bg-gray-100">{t('admin.sub_services.cancel')}</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex gap-2 items-center">
                                            <div className={`w-3 h-3 rounded-full shrink-0 ${col.dot}`} />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs font-bold text-gray-800 truncate">{g.name}</p>
                                                <p className="text-[9px] text-gray-500">{t('admin.sub_services.sub_service_count', { count })}</p>
                                            </div>
                                            <div className="flex gap-1 shrink-0">
                                                <button onClick={() => { setEditingId(g.id); setEditName(g.name); setEditColor(g.color) }}
                                                    className="p-1 text-gray-400 rounded transition-all hover:text-primary hover:bg-white/60">
                                                    <FaEdit size={10} />
                                                </button>
                                                <button onClick={() => setConfirmDel(g)}
                                                    className="p-1 text-gray-400 rounded transition-all hover:text-red-500 hover:bg-white/60">
                                                    <FaTrash size={10} />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )
                        })}

                        <button
                            onClick={() => setAddingNew(true)}
                            className="rounded-xl border-2 border-dashed border-gray-200 p-3 flex items-center justify-center gap-1.5 text-xs text-gray-400 hover:border-primary/30 hover:text-primary hover:bg-primary/5 transition-all"
                        >
                            <FaPlus size={9} /> {t('admin.sub_services.new_group')}
                        </button>
                    </div>

                    {confirmDel && (
                        <ConfirmDialog
                            title={t('admin.sub_services.delete_group_title', { name: confirmDel.name })}
                            body={t('admin.sub_services.delete_group_body', { count: countByGroup[confirmDel.id] || 0 })}
                            onConfirm={() => handleDelete(confirmDel)}
                            onCancel={() => setConfirmDel(null)}
                        />
                    )}
                </div>
            )}
        </div>
    )
}

// ── MAIN TAB ──────────────────────────────────────────────────────────────────
export default function CategoriesSubServicesTab({ canEdit, session }) {
    const { t } = useTranslation()
    const { items: catalogItems, upsertItem, reload: reloadCatalog } = useCatalogItems(session)
    const {
        items: subServices, loading: ssLoading, error: ssError,
        createSubService, updateSubService, updateSubServiceText, removeSubService, reload: reloadSS
    } = useSubServices(session)
    const {
        groups, loading: grpLoading,
        createGroup, updateGroup, deleteGroup, reload: reloadGroups
    } = useSubServiceGroups()
    const {
        categories, createCategory, updateCategory, deleteCategory, reload: reloadCats
    } = useCategories()
    const { units, addUnit, removeUnit } = useUnits()

    const [search,      setSearch]      = useState('')
    const [groupFilter, setGroupFilter] = useState('all')
    const [addingNew,   setAddingNew]   = useState(false)
    const [newText,     setNewText]     = useState('')
    const [newGroup,    setNewGroup]    = useState('')
    const [newCat,      setNewCat]      = useState('')
    const [creating,    setCreating]    = useState(false)
    const [newUnit,     setNewUnit]     = useState('')

    const subServiceMap = useMemo(() => buildSubServiceMap(subServices), [subServices])
    const usageCounts   = useMemo(() => computeUsageCounts(subServices, catalogItems), [subServices, catalogItems])

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase()
        return subServices.filter(s => {
            const matchGroup = groupFilter === 'all'
                ? true
                : groupFilter === '__ungrouped__'
                    ? !s.group_id
                    : s.group_id === groupFilter
            const matchSearch = !q || s.text.toLowerCase().includes(q)
            return matchGroup && matchSearch
        })
    }, [subServices, search, groupFilter])

    // Handle sub-service update — cascades text to catalog items when text changed
    const handleUpdateSubService = useCallback(async (id, patch) => {
        const { _textChanged, text, group_id } = patch
        if (_textChanged && text !== undefined) {
            await updateSubServiceText(id, text, catalogItems, upsertItem)
            if (group_id !== undefined) {
                const current = subServices.find(s => s.id === id)
                if (current?.group_id !== (group_id || null)) {
                    await updateSubService(id, { group_id: group_id || null })
                }
            }
        } else {
            const { _textChanged: _, ...cleanPatch } = patch
            await updateSubService(id, cleanPatch)
        }
    }, [updateSubService, updateSubServiceText, catalogItems, upsertItem, subServices])

    // Handle cascade delete of a sub-service
    const handleDeleteSubService = useCallback(async (id, usageItems) => {
        // Strip this id from all affected catalog items
        const affected = [...new Set(usageItems.map(it => it.id))]
        for (const itemId of affected) {
            const item = catalogItems.find(it => it.id === itemId)
            if (!item) continue
            const nextInc = (item.inc_ids || []).filter(i => i !== id)
            const nextExc = (item.exc_ids || []).filter(i => i !== id)
            const incTexts = nextInc.map(i => subServiceMap[i]?.text || '')
            const excTexts = nextExc.map(i => subServiceMap[i]?.text || '')
            await upsertItem({ ...item, inc_ids: nextInc, inc: incTexts, exc_ids: nextExc, exc: excTexts })
        }
        await removeSubService(id)
    }, [catalogItems, subServiceMap, upsertItem, removeSubService])

    // Handle category deletion with reassignment
    const handleCategoryReload = useCallback(async (action, item, targetCatName) => {
        if (action === 'reassign') {
            await upsertItem({ ...item, cat: targetCatName })
        }
    }, [upsertItem])

    const handleCreateSubService = async () => {
        const text = newText.trim()
        if (!text) return
        setCreating(true)
        try {
            await createSubService({ text, group_id: newGroup || null, cat: newCat || null })
            setNewText(''); setNewGroup(''); setNewCat('')
            setAddingNew(false)
        } catch (e) { console.error(e) }
        finally { setCreating(false) }
    }

    const ungroupedCount = useMemo(() => subServices.filter(s => !s.group_id).length, [subServices])

    if (ssLoading || grpLoading) {
        return (
            <div className="flex gap-3 justify-center items-center py-24 text-gray-400">
                <FaSync size={16} className="animate-spin" />
                <span className="text-sm">{t('admin.sub_services.loading')}</span>
            </div>
        )
    }

    return (
        <div className="space-y-8">

            {/* ── Unit Types ── */}
            <section className="p-5 bg-white rounded-2xl border border-gray-200 shadow-sm">
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <h3 className="text-sm font-bold text-gray-700">Unit Types</h3>
                        <p className="text-[11px] text-gray-400 mt-0.5">{units.length} type{units.length !== 1 ? 's' : ''} · appears in catalog item dropdowns</p>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2 mb-4">
                    {units.map(u => (
                        <span key={u} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border bg-gray-50 text-gray-700 border-gray-200">
                            {u}
                            <button
                                type="button"
                                onClick={() => removeUnit(u)}
                                className="text-gray-400 transition-colors hover:text-red-500"
                                title={`Remove ${u}`}
                            >
                                <FaTimes size={9} />
                            </button>
                        </span>
                    ))}
                    {units.length === 0 && (
                        <span className="text-xs italic text-gray-400">No unit types defined</span>
                    )}
                </div>
                <form
                    onSubmit={e => { e.preventDefault(); addUnit(newUnit); setNewUnit('') }}
                    className="flex gap-2 items-center"
                >
                    <input
                        type="text"
                        value={newUnit}
                        onChange={e => setNewUnit(e.target.value)}
                        placeholder="New unit (e.g. LF, SY, EA)"
                        className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40 flex-1 max-w-xs"
                    />
                    <button
                        type="submit"
                        disabled={!newUnit.trim()}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white text-xs font-bold rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-all"
                    >
                        <FaPlus size={9} /> Add
                    </button>
                </form>
            </section>

            {/* ── Categories ── */}
            <section className="p-5 bg-white rounded-2xl border border-gray-200 shadow-sm">
                <CategoriesSection
                    categories={categories}
                    catalogItems={catalogItems}
                    createCategory={createCategory}
                    updateCategory={updateCategory}
                    deleteCategory={deleteCategory}
                    reload={handleCategoryReload}
                />
            </section>

            {/* ── Sub-Service Groups ── */}
            <section className="p-5 bg-white rounded-2xl border border-gray-200 shadow-sm">
                <GroupsSection
                    groups={groups}
                    subServices={subServices}
                    createGroup={createGroup}
                    updateGroup={updateGroup}
                    deleteGroup={deleteGroup}
                />
            </section>

            {/* ── Sub-Services ── */}
            <section className="bg-white rounded-2xl border border-gray-200 shadow-sm">
                {/* Header */}
                <div className="flex flex-col gap-3 px-5 pt-5 pb-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h3 className="text-sm font-bold text-gray-700">{t('admin.sub_services.sub_services_title')}</h3>
                        <p className="text-[11px] text-gray-400 mt-0.5">{t('admin.sub_services.sub_services_subtitle', { total: subServices.length, unused: subServices.filter(s => (usageCounts.get(s.id)?.total ?? 0) === 0).length })}</p>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={() => { reloadSS(); reloadGroups() }} className="p-2 text-gray-400 rounded-xl border border-gray-200 transition-all hover:text-gray-600 hover:bg-gray-50" title={t('admin.sub_services.reload')}>
                            <FaSync size={11} />
                        </button>
                        <button
                            onClick={() => setAddingNew(p => !p)}
                            className="flex items-center gap-1.5 px-3 py-2 bg-primary text-white text-xs font-bold rounded-xl hover:bg-primary/90 transition-all shadow-sm"
                        >
                            <FaPlus size={10} /> {t('admin.sub_services.new_sub_service')}
                        </button>
                    </div>
                </div>

                {/* Add new form */}
                {addingNew && (
                    <div className="p-4 mx-5 mb-4 space-y-3 rounded-xl border border-primary/20 bg-primary/5">
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                            <div className="sm:col-span-2">
                                <label className="block text-[10px] text-gray-500 mb-1">{t('admin.sub_services.label_text')}</label>
                                <input autoFocus type="text" value={newText} onChange={e => setNewText(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') handleCreateSubService(); if (e.key === 'Escape') setAddingNew(false) }}
                                    placeholder={t('admin.sub_services.placeholder_text')}
                                    className="px-3 py-1.5 w-full text-xs rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-primary/40"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] text-gray-500 mb-1">{t('admin.sub_services.label_group')}</label>
                                <select value={newGroup} onChange={e => setNewGroup(e.target.value)}
                                    className="px-3 py-1.5 w-full text-xs rounded-lg border border-gray-200 focus:outline-none bg-white">
                                    <option value="">{t('admin.sub_services.ungrouped')}</option>
                                    {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                            <div>
                                <label className="block text-[10px] text-gray-500 mb-1">{t('admin.sub_services.label_cat_hint')}</label>
                                <select value={newCat} onChange={e => setNewCat(e.target.value)}
                                    className="px-3 py-1.5 w-full text-xs rounded-lg border border-gray-200 focus:outline-none bg-white">
                                    <option value="">{t('admin.sub_services.option_all_cats')}</option>
                                    {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="flex gap-1.5">
                            <button onClick={handleCreateSubService} disabled={!newText.trim() || creating}
                                className="px-4 py-1.5 bg-primary text-white text-xs font-bold rounded-lg disabled:opacity-50 transition-all">
                                {creating ? t('admin.sub_services.creating') : t('admin.sub_services.create')}
                            </button>
                            <button onClick={() => { setAddingNew(false); setNewText('') }}
                                className="px-3 py-1.5 bg-white text-gray-600 text-xs font-bold rounded-lg border border-gray-200 hover:bg-gray-50 transition-all">{t('admin.sub_services.cancel')}</button>
                        </div>
                    </div>
                )}

                {/* Search + group filter */}
                <div className="flex flex-col gap-2 px-5 pb-3 sm:flex-row sm:items-center">
                    <div className="relative flex-1">
                        <FaSearch className="absolute left-3 top-1/2 text-gray-400 -translate-y-1/2" size={11} />
                        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                            placeholder={t('admin.sub_services.search_placeholder')}
                            className="pl-8 pr-3 py-1.5 w-full text-xs rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                        />
                    </div>
                    <div className="flex flex-wrap gap-1">
                        <button onClick={() => setGroupFilter('all')}
                            className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border transition-all ${groupFilter === 'all' ? 'bg-gray-700 text-white border-gray-700' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}>
                            {t('admin.sub_services.filter_all')}
                        </button>
                        {groups.map(g => (
                            <button key={g.id} onClick={() => setGroupFilter(groupFilter === g.id ? 'all' : g.id)}
                                className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border transition-all ${groupFilter === g.id ? `${COLOR_CLASSES[g.color]?.badge ?? 'bg-gray-100 text-gray-700 border-gray-200'} shadow-sm` : 'bg-white text-gray-400 border-gray-100 hover:bg-gray-50'}`}>
                                {g.name}
                            </button>
                        ))}
                        {ungroupedCount > 0 && (
                            <button onClick={() => setGroupFilter(groupFilter === '__ungrouped__' ? 'all' : '__ungrouped__')}
                                className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border transition-all ${groupFilter === '__ungrouped__' ? 'bg-gray-200 text-gray-700 border-gray-300 shadow-sm' : 'bg-white text-gray-400 border-gray-100 hover:bg-gray-50'}`}>
                                {t('admin.sub_services.filter_ungrouped', { count: ungroupedCount })}
                            </button>
                        )}
                    </div>
                </div>

                {ssError && (
                    <p className="px-3 py-2 mx-5 mb-3 text-xs text-amber-600 bg-amber-50 rounded-lg border border-amber-200">
                        {t('admin.sub_services.load_error', { error: ssError })}
                    </p>
                )}

                {/* List */}
                <div className="overflow-hidden rounded-b-2xl border-t border-gray-100">
                    {filtered.length === 0 ? (
                        <div className="py-10 text-sm text-center text-gray-400">
                            {search ? t('admin.sub_services.no_match', { query: search }) : t('admin.sub_services.empty')}
                        </div>
                    ) : (
                        filtered.map(sub => (
                            <SubServiceRow
                                key={sub.id}
                                sub={sub}
                                groups={groups}
                                usageEntry={usageCounts.get(sub.id)}
                                allSubServices={subServices}
                                allCatalogItems={catalogItems}
                                upsertItem={upsertItem}
                                onUpdate={handleUpdateSubService}
                                onDelete={handleDeleteSubService}
                            />
                        ))
                    )}
                </div>
            </section>
        </div>
    )
}
